import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthLayout, { CornerLogo, AUTH } from './AuthLayout';

type TabMode = 'draw' | 'type' | 'upload';

export default function SignatureSetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const fromSettings = (location.state as { from?: string } | null)?.from === 'settings';
  const returnTo = useMemo(() => {
    const q = new URLSearchParams(location.search).get('returnTo');
    return q && q.startsWith('/') ? q : null;
  }, [location.search]);
  const [activeTab, setActiveTab] = useState<TabMode>('draw');
  const [typedName, setTypedName] = useState('');
  const [hasDrawn, setHasDrawn] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);

  const getCanvasDataUrl = useCallback(() => {
    return canvasRef.current?.toDataURL('image/png') || null;
  }, []);

  // Init canvas whenever the draw tab is active (handles remount after tab switch).
  // Defer one frame so layout gives a non-zero height (flex/layout races on some browsers).
  useEffect(() => {
    if (activeTab !== 'draw') return;
    let cancelled = false;
    const init = () => {
      if (cancelled) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const rect = canvas.getBoundingClientRect();
      if (rect.width < 2 || rect.height < 2) {
        requestAnimationFrame(init);
        return;
      }
      const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      ctx.strokeStyle = getComputedStyle(document.documentElement).getPropertyValue('--auth-signature-stroke').trim() || '#1A1714';
      ctx.lineWidth = 2.5;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    };
    requestAnimationFrame(init);
    return () => { cancelled = true; };
  }, [activeTab]);

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    if ('touches' in e) {
      const touch = e.touches[0] || e.changedTouches[0];
      return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const startDraw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    if ('touches' in e) e.preventDefault(); // prevent scroll while drawing
    isDrawing.current = true;
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    if ('touches' in e) e.preventDefault();
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const endDraw = () => { isDrawing.current = false; };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    // clearRect is in device pixels; ignore current scale transform.
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const rect = canvas.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) {
      setHasDrawn(false);
      return;
    }
    const dpr = canvas.width / rect.width;
    ctx.scale(dpr, dpr);
    ctx.strokeStyle = getComputedStyle(document.documentElement).getPropertyValue('--auth-signature-stroke').trim() || '#1A1714';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    setHasDrawn(false);
  };

  const handleSkip = () => {
    if (fromSettings) {
      navigate('/app/settings');
    } else if (returnTo) {
      navigate(returnTo, { replace: true });
    } else {
      navigate('/auth/welcome');
    }
  };

  const handleSave = () => {
    let dataUrl: string | null = null;
    if (activeTab === 'draw') {
      dataUrl = getCanvasDataUrl();
    } else if (activeTab === 'type') {
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = 600;
      tempCanvas.height = 200;
      const ctx = tempCanvas.getContext('2d');
      if (ctx) {
        ctx.font = '48px "Dancing Script", cursive';
        ctx.fillStyle = getComputedStyle(document.documentElement).getPropertyValue('--auth-signature-stroke').trim() || '#1A1714';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(typedName, 300, 100);
        dataUrl = tempCanvas.toDataURL('image/png');
      }
    } else if (activeTab === 'upload') {
      dataUrl = uploadedImage;
    }
    if (dataUrl) {
      sessionStorage.setItem('corner:pending-signature', dataUrl);
      navigate('/auth/signature-verify', {
        state: {
          ...(fromSettings ? { from: 'settings' as const } : {}),
          ...(returnTo ? { returnTo } : {}),
        },
      });
    }
  };

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setUploadedImage(reader.result as string); };
    reader.readAsDataURL(file);
  };

  const canSave =
    (activeTab === 'draw' && hasDrawn) ||
    (activeTab === 'type' && typedName.trim().length > 0) ||
    (activeTab === 'upload' && uploadedImage);

  const tabs: { key: TabMode; label: string }[] = [
    { key: 'draw', label: 'Draw' },
    { key: 'type', label: 'Type' },
    { key: 'upload', label: 'Upload' },
  ];

  return (
    <AuthLayout>
      {/* 520px card — wider than auth card */}
      <div
        style={{
          width: '100%',
          maxWidth: 520,
          backgroundColor: AUTH.cardBg,
          borderRadius: 16,
          border: `1px solid ${AUTH.cardBorder}`,
          boxShadow: AUTH.cardShadow,
          overflow: 'hidden',
          fontFamily: '"Geist", sans-serif',
        }}
      >
        {/* Header — logo + title row + subtitle */}
        <div
          style={{
            padding: '24px 28px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CornerLogo size={24} />
              <span style={{ fontSize: 16, fontWeight: 700, color: AUTH.textPrimary, marginLeft: -4 }}>
                {fromSettings ? 'Edit signature' : 'Set up your signature'}
              </span>
            </div>
            {fromSettings ? (
              <button
                type="button"
                onClick={handleSkip}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={AUTH.textMuted} strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            ) : (
              <span style={{ fontSize: 13, color: AUTH.textMuted, fontWeight: 400 }}>
                Step 1 of 1
              </span>
            )}
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              fontWeight: 400,
              color: AUTH.textMuted,
              lineHeight: '16px',
              paddingLeft: 30,
            }}
          >
            Used for e-signing documents — you can change this anytime in Settings.
          </p>
        </div>

        {/* Divider */}
        <div style={{ height: 1, backgroundColor: '#F0EDE8' }} />

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #F0EDE8' }}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                flex: 1,
                padding: '12px 0',
                background: 'none',
                border: 'none',
                borderBottom: `2px solid ${activeTab === tab.key ? AUTH.accent : 'transparent'}`,
                fontSize: 13,
                fontWeight: activeTab === tab.key ? 600 : 400,
                color: activeTab === tab.key ? '#D46A3A' : AUTH.textMuted,
                cursor: 'pointer',
                fontFamily: '"Geist", sans-serif',
                marginBottom: -1,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Canvas / Input area */}
        <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {activeTab === 'draw' && (
            <>
              <div
                style={{
                  height: 200,
                  border: '1.5px dashed #D4CAC0',
                  borderRadius: 10,
                  backgroundColor: '#FFFFFF',
                  overflow: 'hidden',
                  position: 'relative',
                }}
              >
                <canvas
                  ref={canvasRef}
                  onMouseDown={startDraw}
                  onMouseMove={draw}
                  onMouseUp={endDraw}
                  onMouseLeave={endDraw}
                  onTouchStart={startDraw}
                  onTouchMove={draw}
                  onTouchEnd={endDraw}
                  onTouchCancel={endDraw}
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: 0,
                    cursor: 'crosshair',
                    display: 'block',
                    touchAction: 'none',
                    backgroundColor: '#FFFFFF',
                  }}
                />
                {hasDrawn && (
                  <button
                    onClick={clearCanvas}
                    style={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      padding: '4px 14px',
                      fontSize: 13,
                      fontWeight: 500,
                      backgroundColor: AUTH.inputBg,
                      border: `1px solid ${AUTH.cardBorder}`,
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontFamily: '"Geist", sans-serif',
                      color: AUTH.textPrimary,
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
              <p style={{ margin: 0, fontSize: 13, color: AUTH.textMuted, textAlign: 'center', lineHeight: '16px' }}>
                Draw your signature above using your mouse or trackpad
              </p>
            </>
          )}

          {activeTab === 'type' && (
            <>
              <div
                style={{
                  height: 180,
                  border: '1.5px dashed #D4CAC0',
                  borderRadius: 10,
                  backgroundColor: '#FAFAF9',
                  padding: '40px 24px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  boxSizing: 'border-box',
                }}
              >
                <input
                  type="text"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder="Type your name"
                  style={{
                    width: '100%',
                    border: 'none',
                    outline: 'none',
                    textAlign: 'center',
                    fontSize: 36,
                    fontFamily: '"Dancing Script", cursive',
                    color: AUTH.textPrimary,
                    backgroundColor: 'transparent',
                  }}
                  autoFocus
                />
                <div style={{ width: '80%', height: 1, backgroundColor: '#E8E2DA', marginTop: 8 }} />
              </div>
              <p style={{ margin: 0, fontSize: 13, color: AUTH.textMuted, textAlign: 'center', lineHeight: '16px' }}>
                Type your name and we&apos;ll generate a signature
              </p>
            </>
          )}

          {activeTab === 'upload' && (
            <>
              <label
                style={{
                  height: 180,
                  border: '1.5px dashed #D4CAC0',
                  borderRadius: 10,
                  backgroundColor: '#FAFAF9',
                  padding: '40px 24px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                }}
              >
                <input type="file" accept="image/*" onChange={handleUpload} style={{ display: 'none' }} />
                {uploadedImage ? (
                  <img src={uploadedImage} alt="Uploaded signature" style={{ maxWidth: '100%', maxHeight: 120, objectFit: 'contain' }} />
                ) : (
                  <>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={AUTH.textSecondary} strokeWidth="1.5">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <p style={{ margin: '12px 0 0', fontSize: 14, color: AUTH.textSecondary }}>Click to upload an image</p>
                  </>
                )}
              </label>
              <p style={{ margin: 0, fontSize: 13, color: AUTH.textMuted, textAlign: 'center', lineHeight: '16px' }}>
                Upload a PNG or JPG of your signature
              </p>
            </>
          )}
        </div>

        {/* Divider */}
        <div style={{ height: 1, backgroundColor: '#F0EDE8' }} />

        {/* Footer buttons */}
        <div
          style={{
            padding: '16px 28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <button
            onClick={handleSkip}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              fontSize: 13,
              fontWeight: 400,
              color: AUTH.textMuted,
              cursor: 'pointer',
              fontFamily: '"Geist", sans-serif',
              lineHeight: '16px',
            }}
          >
            {fromSettings ? 'Cancel' : 'Skip for now'}
          </button>

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => {
                if (activeTab === 'draw') clearCanvas();
                if (activeTab === 'type') setTypedName('');
                if (activeTab === 'upload') setUploadedImage(null);
              }}
              style={{
                height: 38,
                padding: '0 16px',
                fontSize: 13,
                fontWeight: 500,
                backgroundColor: 'transparent',
                border: `1px solid #E4DDD4`,
                borderRadius: 8,
                cursor: 'pointer',
                fontFamily: '"Geist", sans-serif',
                color: AUTH.textPrimary,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Redo
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave}
              style={{
                height: 38,
                padding: '0 20px',
                fontSize: 13,
                fontWeight: 600,
                backgroundColor: canSave ? AUTH.accent : '#E8C4B0',
                border: 'none',
                borderRadius: 8,
                cursor: canSave ? 'pointer' : 'not-allowed',
                fontFamily: '"Geist", sans-serif',
                color: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              Save signature &rarr;
            </button>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
}
